import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useAdvertsStore } from '../stores/adverts.store';
import { useCases } from '../../infrastructure/di/container';
import { AdvertEntity, AdvertTitle, AttachmentProps } from '../../domain';
import { User } from 'firebase/auth';

interface UseAdvertDetailsOptions { loadOnMount?: boolean; }

export const useAdvertDetails = (user: User | undefined, uuid?: string, options: UseAdvertDetailsOptions = {}) => {
  const { loadOnMount = true } = options;
  const { current, isLoading } = useAdvertsStore();
  const createUC = useCases.createAdvert();
  const updateUC = useCases.updateAdvert();
  const deleteUC = useCases.deleteAdvert();
  const draftRef = useRef<AdvertEntity | null>(null);
  // Track a version to force rerenders and memo recomputation when draft changes
  const [draftVersion, setDraftVersion] = useState(0);
  const [error, setError] = useState<string | undefined>();

  const toDraftTitle = useCallback((raw: string): AdvertTitle => {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) {
      return ({ get Value() { return '' } } as unknown) as AdvertTitle;
    }
    return AdvertTitle.create(trimmed);
  }, []);

  // Initialize draft for create flow
  useEffect(() => {
    if (!uuid && !draftRef.current) {
  draftRef.current = AdvertEntity.create({
        uuid: '',
        id: 0,
        title: toDraftTitle(''),
        description: '',
        accountId: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        attachments: [] as AttachmentProps[]
      });
  setDraftVersion(v => v + 1);
    }
  }, [uuid, toDraftTitle]);

  // Load advert for edit only once per uuid
  const lastLoadedUuidRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (uuid && loadOnMount && lastLoadedUuidRef.current !== uuid) {
      lastLoadedUuidRef.current = uuid;
  useAdvertsStore.getState().get(uuid).catch(e => setError(e?.message || 'load-error'));
    }
  }, [uuid, loadOnMount]);

  // Initialize draft once from current after load
  useEffect(() => {
    if (uuid && current && current.uuid === uuid && !draftRef.current) {
      draftRef.current = current as AdvertEntity;
      setDraftVersion(v => v + 1);
    }
  }, [uuid, current]);

  // Exposed model prefers draft (so UI reflects unsaved edits) falling back to adapted entity for safety
  const advertModel: AdvertEntity = useMemo(() => {
    if (draftRef.current) return draftRef.current as AdvertEntity;
    if (uuid && current && current.uuid === uuid) return current as AdvertEntity;
    return AdvertEntity.create({
      uuid: '',
      id: 0,
      title: toDraftTitle(''),
      description: '',
      accountId: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      attachments: [] as AttachmentProps[]
    });
  }, [uuid, current, toDraftTitle, draftVersion]);

  const setTitle = useCallback((title: string) => {
    const base = draftRef.current || advertModel;
    const next = (title ?? '').trim();
    if ((base.title?.Value || '') === next) return;
    draftRef.current = base.withTitle(toDraftTitle(title));
    setDraftVersion(v => v + 1);
  }, [advertModel, toDraftTitle]);

  const setDescription = useCallback((desc: string) => {
    const base = draftRef.current || advertModel;
    const next = desc ?? '';
    if ((base.description || '') === next) return;
    draftRef.current = base.withDescription(next);
    setDraftVersion(v => v + 1);
  }, [advertModel]);

  const uploadFile = useCallback(async (file: File, position: number) => {
    try {
      const objectUrl = URL.createObjectURL(file);
      const previewId = `local-${Date.now()}-${position}`;
      const attachment = ({ uuid: previewId, url: objectUrl, type: (file.type as any) || 'other', position, file } as unknown) as AttachmentProps;
      const base = draftRef.current || advertModel;
      draftRef.current = base.addAttachment(attachment);
      setDraftVersion(v => v + 1);
      return attachment;
    } catch (e: any) {
      setError(e?.message);
      throw e;
    }
  }, [advertModel]);

  const removeAttachment = useCallback((attachment: AttachmentProps) => {
    const base = draftRef.current || advertModel;
    draftRef.current = base.removeAttachment(attachment);
    setDraftVersion(v => v + 1);
  }, [advertModel]);

  const reorderAttachments = useCallback((attachments: AttachmentProps[]) => {
    const base = draftRef.current || advertModel;
    draftRef.current = base.reorderAttachments(attachments);
    setDraftVersion(v => v + 1);
  }, [advertModel]);

  const createAdvert = useCallback(async () => {
    if (!draftRef.current) return null;
    try {
      const count = (draftRef.current.attachments || []).length;
      if (count > 10) {
        throw new Error('advert-files-limit-exceeded');
      }
      const files = (draftRef.current.attachments || []).map((a: AttachmentProps & { file?: File }) => a as any).map(a => a.file).filter(Boolean) as File[];
      const entity = await createUC.execute({ title: draftRef.current.title.Value, description: draftRef.current.description, attachments: files.map(f => ({ file: f, mimeType: (f as File).type })) });
      return entity;
    } catch (e: any) { setError(e?.message); throw e; }
  }, [createUC]);

  const updateAdvert = useCallback(async () => {
    if (!uuid) return null;
    try {
      const model = (draftRef.current || (current as AdvertEntity)) as AdvertEntity;
      if (!model) return null;
      if ((model.attachments || []).length > 10) {
        throw new Error('advert-files-limit-exceeded');
      }

      const ordered = (model.attachments || []).slice().sort((a, b) => a.position - b.position) as (AttachmentProps & { file?: File })[];
      const uploads: { file: File | Blob; mimeType: string; position: number }[] = [];
      const keepAttachments: { uuid: string; position: number }[] = [];
      ordered.forEach((attachment, position) => {
        if (attachment.file) {
          uploads.push({ file: attachment.file, mimeType: attachment.file.type || 'application/octet-stream', position });
        } else if (attachment.uuid) {
          keepAttachments.push({ uuid: attachment.uuid, position });
        }
      });

      const entity = await updateUC.execute({ uuid: model.uuid, title: model.title.Value, description: model.description, attachments: uploads, keepAttachments });
      if (!entity) return null;
      draftRef.current = entity;
      setDraftVersion(v => v + 1);
      useAdvertsStore.getState().get(entity.uuid).catch(() => {});
      return draftRef.current;
  } catch (e: any) { setError(e?.message); throw e; }
  }, [uuid, current, updateUC]);

  const deleteAdvert = useCallback(async (uuidToDelete: string) => {
    try { await deleteUC.execute(uuidToDelete); return true; } catch { return false; }
  }, [deleteUC]);

  const setAdvertChannels = useCallback(async (advertUUID: string, channelUUIDs: string[], telegramAccountUUIDs?: string[]) => {
    if (!advertUUID) return;
    try {
      await useCases.setAdvertChannels().execute(advertUUID, channelUUIDs, telegramAccountUUIDs);
    } catch (e: any) {
      setError(e?.message || 'channels-assign-failed');
      throw e;
    }
  }, []);

  const submitAdvert = useCallback(async (channelUUIDs: string[], telegramAccountUUIDs?: string[]) => {
    const isEditMode = !!uuid;
    
    const entity = isEditMode ? await updateAdvert() : await createAdvert();
    if (!entity) {
      throw new Error(isEditMode ? 'advert-update-failed' : 'advert-create-failed');
    }
    
    await setAdvertChannels(entity.uuid, channelUUIDs, telegramAccountUUIDs);
    
    return entity;
  }, [uuid, createAdvert, updateAdvert, setAdvertChannels]);

  return {
  advert: advertModel,
    loading: isLoading,
    error,
    createAdvert,
    updateAdvert,
    deleteAdvert,
    uploadFile,
    removeAttachment,
    setTitle,
    setDescription,
    reorderAttachments,
    setAdvertChannels,
    submitAdvert
  };
};
